/* Botanical Tenant Home — verdant, garden-pressed, organic.

   Emotional intent: NATURAL/GROWTH. Soft sage green canvas. Photos with
   ROUNDED corners (NOT square like Brutalist, NOT framed like Heirloom).
   Section dividers are SVG wave curves. Headlines feel hand-lettered.
   Photos slightly tilted (rotate-[0.5deg]) for hand-pasted scrapbook feel.
   Eucalyptus-leaf flourishes between sections. Mosaic portfolio.

   Structurally distinct: rounded everything, wave dividers, tilted images,
   organic mosaic grid (not strict), "blossoms" service treatment with
   pill-shaped CTAs. */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Leaf } from "lucide-react";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { tenantHref } from "@/lib/tenant";
import { optimizeImageUrl } from "@/lib/imageOptim";
import { EditorialSkeleton } from "../editorial/_EditorialSkeleton";

const FALLBACK = "https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=2000&q=80";

const WaveDivider = ({ color }) => (
  <div className="overflow-hidden leading-none">
    <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="w-full h-12" aria-hidden>
      <path d="M0,30 C240,55 480,5 720,30 C960,55 1200,5 1440,30 L1440,60 L0,60 Z" fill={color} />
    </svg>
  </div>
);

export default function BotanicalTenantHome() {
  const { settings, loading } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const heroImg = settings?.hero_image || settings?.portfolio_images?.[0] || FALLBACK;
  const heroPos = `${settings?.hero_focal_x ?? 50}% ${settings?.hero_focal_y ?? 50}%`;
  const headline = settings?.hero_title_line1 || "Where the light";
  const subhead = settings?.hero_title_line2 || "grows softly.";
  const intro = settings?.hero_description || `${studioName} photographs outdoor weddings, garden portraits, and lifestyle work where things bloom slowly and the eucalyptus is fresh-cut.`;
  const aboutTitle = settings?.about_title || "From the garden.";
  const aboutBody = settings?.about_body || "We shoot under leafy canopies, in pollinator gardens, and at golden hour on the lawn. Every session is paced like a long Sunday — never rushed, always tended.";
  const port = (settings?.portfolio_images || []).slice(0, 5);

  const [services, setServices] = useState([]);
  useEffect(() => {
    api.get("/services").then((r) => setServices(Array.isArray(r.data) ? r.data.slice(0, 3) : [])).catch(() => {});
  }, []);
  useEffect(() => { document.title = `${studioName} — Botanical`; }, [studioName]);

  if (loading && !settings) return <EditorialSkeleton eyebrow="Watering the garden." />;

  const tiltClass = (i) => ["", "-rotate-1", "rotate-1", "-rotate-[0.5deg]", "rotate-[0.5deg]"][i % 5];

  return (
    <div data-testid="botanical-tenant-home"
      style={{ background: "var(--tenant-bg, #F6F4EE)", color: "var(--tenant-fg, #2A3A20)" }}>

      {/* ── HERO — rounded photo with wave bottom + leaf flourish ───── */}
      <section className="px-6 lg:px-12 pt-12 pb-16" data-testid="botanical-hero">
        <div className="max-w-[1280px] mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-3 mb-6">
              <Leaf className="w-5 h-5" strokeWidth={1.25} style={{ color: "var(--tenant-accent)" }} />
              <div className="text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-accent)" }}>
                Garden-Pressed · Outdoor Portraits
              </div>
              <Leaf className="w-5 h-5 -scale-x-100" strokeWidth={1.25} style={{ color: "var(--tenant-accent)" }} />
            </div>
            <h1 className="leading-[1.02]" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.5rem, 6.5vw, 5.5rem)" }}>
              {headline}{" "}
              <em className="italic" style={{ color: "var(--tenant-accent)" }}>{subhead}</em>
            </h1>
            <p className="mt-7 max-w-xl mx-auto text-base leading-relaxed" style={{ color: "var(--tenant-fg-soft)" }}>
              {intro}
            </p>
          </div>
          {/* Big rounded photo with wave bottom */}
          <div className="relative">
            <img src={optimizeImageUrl(heroImg, { w: 2000 })}
              alt={`${studioName} garden`}
              className="w-full aspect-[16/10] object-cover rounded-[40px]"
              style={{ objectPosition: heroPos }}
              loading="eager" fetchPriority="high" decoding="async" />
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link to={tenantHref("/booking")} data-testid="botanical-hero-book"
              className="inline-flex items-center gap-3 px-8 py-4 text-[11px] uppercase tracking-[0.32em] rounded-full"
              style={{ background: "var(--tenant-accent)", color: "var(--tenant-bg, #F6F4EE)" }}>
              Tend a session →
            </Link>
            <Link to={tenantHref("/portfolio")} className="text-[11px] uppercase tracking-[0.32em] rounded-full px-6 py-4 border"
                  style={{ borderColor: "var(--tenant-accent)", color: "var(--tenant-accent)" }}>
              Wander the garden
            </Link>
          </div>
        </div>
      </section>

      {/* ── Wave divider into about ──────────────────────────────────── */}
      <WaveDivider color="color-mix(in srgb, var(--tenant-accent, #7D9E68) 18%, var(--tenant-bg, #F6F4EE))" />

      {/* ── FROM THE GARDEN (about) ─────────────────────────────────── */}
      <section className="px-6 lg:px-12 py-20"
               style={{ background: "color-mix(in srgb, var(--tenant-accent, #7D9E68) 18%, var(--tenant-bg, #F6F4EE))" }}>
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-[10px] uppercase tracking-[0.42em] mb-4" style={{ color: "var(--tenant-accent)" }}>
            ❀ From the Garden
          </div>
          <h2 className="leading-tight" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(1.75rem, 4vw, 3rem)" }}>
            <em className="italic">{aboutTitle}</em>
          </h2>
          <p className="mt-7 text-base leading-loose max-w-xl mx-auto" style={{ color: "var(--tenant-fg-soft)" }}>
            {aboutBody}
          </p>
        </div>
      </section>

      <WaveDivider color="var(--tenant-bg, #F6F4EE)" />

      {/* ── BLOSSOMS (services) — pill cards ─────────────────────────── */}
      {services.length > 0 && (
        <section className="px-6 lg:px-12 py-20" data-testid="botanical-services">
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center mb-14">
              <div className="text-[10px] uppercase tracking-[0.42em] mb-3" style={{ color: "var(--tenant-accent)" }}>
                ❀ Blossoms
              </div>
              <h2 className="leading-tight" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(1.75rem, 4vw, 2.75rem)" }}>
                <em className="italic">Sessions in season.</em>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {services.map((svc, i) => (
                <article key={svc.id} className={`p-7 rounded-[32px] transform ${tiltClass(i + 1)} transition-transform hover:rotate-0`}
                         style={{ background: "color-mix(in srgb, var(--tenant-accent, #7D9E68) 10%, var(--tenant-bg, #F6F4EE))" }}>
                  <div className="text-[10px] uppercase tracking-[0.42em] mb-3" style={{ color: "var(--tenant-accent)" }}>
                    ❀ № {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="text-2xl mb-3" style={{ fontFamily: "var(--tenant-serif)" }}>
                    <em className="italic">{svc.name}</em>
                  </h3>
                  {svc.short_description && (
                    <p className="text-sm leading-relaxed mb-5" style={{ color: "var(--tenant-fg-soft)" }}>{svc.short_description}</p>
                  )}
                  <Link to={tenantHref(`/services#${svc.id || svc.slug || ""}`)}
                    className="inline-block text-[10px] uppercase tracking-[0.32em] px-5 py-2.5 rounded-full"
                    style={{ background: "var(--tenant-accent)", color: "var(--tenant-bg)" }}>
                    Pluck this →
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <WaveDivider color="color-mix(in srgb, var(--tenant-accent, #7D9E68) 12%, var(--tenant-bg, #F6F4EE))" />

      {/* ── PORTFOLIO — organic tilted mosaic ────────────────────────── */}
      {port.length > 0 && (
        <section className="px-6 lg:px-12 py-20"
                 style={{ background: "color-mix(in srgb, var(--tenant-accent, #7D9E68) 12%, var(--tenant-bg, #F6F4EE))" }}>
          <div className="max-w-[1280px] mx-auto">
            <div className="text-center mb-12">
              <div className="text-[10px] uppercase tracking-[0.42em] mb-3" style={{ color: "var(--tenant-accent)" }}>❀ The Garden</div>
              <h2 className="leading-tight" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(1.5rem, 3vw, 2.25rem)" }}>
                <em className="italic">Recent blooms.</em>
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {port.map((src, i) => (
                <img key={i} src={optimizeImageUrl(src, { w: 900 })} alt=""
                  className={`w-full aspect-[4/5] object-cover rounded-[24px] transform ${tiltClass(i)} transition-transform hover:rotate-0 hover:scale-[1.02]`}
                  loading="lazy" decoding="async" />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
